import placeholderImage from 'assets/img/covers/placeholder.jpg';
import { Form, OptionToggleGroup } from 'components';
import { MediaUpload } from 'components/Form/inputs/MediaUpload/MediaUpload';
import SelectedItems from 'pages/ModulePage/components/SelectedItems/SelectedItems';
import SelectItemsModal from 'pages/ModulePage/components/SelectItemsModal/SelectItemsModal';
import { youtubeIdFromInput } from 'utils/youtube';
import { useFormikContext } from 'formik';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const VIDEO_MODES = {
  EXISTING: 'existing',
  CUSTOM: 'custom',
};

const getVideoKey = (video) => video.uid;

const getYoutubeThumbnail = (videoUrl) => {
  if (!videoUrl) return null;

  const youtubeId = youtubeIdFromInput(videoUrl);
  return youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : null;
};

const getCloudinaryVideoThumbnail = (videoFileUrl) => {
  if (!videoFileUrl?.includes('res.cloudinary.com')) {
    return null;
  }

  if (/\.jpg(\?|$)/i.test(videoFileUrl)) {
    return videoFileUrl;
  }

  return `${videoFileUrl.replace(/\.(mp4|mov|webm|m4v|avi|mkv)$/i, '')}.jpg`;
};

const getVideoImage = (video) => {
  if (video.image_url) {
    return video.image_url;
  }

  const youtubeThumbnail = getYoutubeThumbnail(video.video_url);
  if (youtubeThumbnail) {
    return youtubeThumbnail;
  }

  const cloudinaryThumbnail = getCloudinaryVideoThumbnail(video.video_file_url);
  if (cloudinaryThumbnail) {
    return cloudinaryThumbnail;
  }

  return placeholderImage;
};

const filterVideoByName = (item, query) =>
  (item.name || '').toLowerCase().includes(query.toLowerCase());

const getVideoItemCardProps = (video, { t }) => ({
  imageSrc: getVideoImage(video),
  imageAlt: video.name || 'Video',
  title: video.name || null,
  meta: [
    {
      label: t('owner'),
      value: typeof video.owner === 'string' ? video.owner : 'Brand',
    },
  ],
});

const videoSourceOptions = (t) => [
  { label: t('videoUrl'), value: 'url' },
  { label: t('videoFile'), value: 'file' },
];

const VideoCustomForm = () => {
  const { t } = useTranslation('ownerPortal', { keyPrefix: 'pageModules' });
  const { values } = useFormikContext();

  return (
    <>
      <Form.GroupVertical required label={t('name')}>
        <Form.Input name="name" type="text" />
      </Form.GroupVertical>
      <Form.GroupVertical required label={t('videoSource')}>
        <Form.Select name="video_source" options={videoSourceOptions(t)} />
      </Form.GroupVertical>
      {values.video_source === 'url' ? (
        <Form.GroupVertical required label={t('videoUrl')}>
          <Form.Input name="video_url" type="url" placeholder="https://www.youtube.com/..." />
        </Form.GroupVertical>
      ) : (
        <Form.GroupVertical required label={t('videoFile')}>
          <MediaUpload name="video_file" maxFiles={1} accept={{ 'video/*': [] }} />
        </Form.GroupVertical>
      )}
    </>
  );
};

export const createVideoConfig = (useAvailableItems) => {
  const VideoConfig = ({ isEditMode }) => {
    const { t } = useTranslation('ownerPortal', { keyPrefix: 'pageModules' });
    const { values, setFieldValue } = useFormikContext();
    const { items, isLoading } = useAvailableItems();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [search, setSearch] = useState('');

    const selectedItems = useMemo(() => values.selectedItems || [], [values.selectedItems]);

    const filteredItems = useMemo(() => {
      if (!search.trim()) return items;
      return items.filter((item) => filterVideoByName(item, search.trim()));
    }, [items, search]);

    const toggleOptions = useMemo(
      () => [
        {
          value: VIDEO_MODES.EXISTING,
          title: t('videoSourceExisting'),
          description: t('videoSourceExistingDescription'),
        },
        {
          value: VIDEO_MODES.CUSTOM,
          title: t('videoSourceCustom'),
          description: t('videoSourceCustomDescription'),
        },
      ],
      [t],
    );

    const handleModeChange = (mode) => {
      setFieldValue('mode', mode);

      if (mode === VIDEO_MODES.EXISTING) {
        setFieldValue('name', '');
        setFieldValue('video_source', 'url');
        setFieldValue('video_url', '');
        setFieldValue('video_file', []);
        setFieldValue('existing_video_file_id', null);
        setFieldValue('existing_video_file_url', null);
        return;
      }

      setFieldValue('selectedItems', []);
    };

    return (
      <>
        {!isEditMode && (
          <OptionToggleGroup
            value={values.mode}
            onChange={handleModeChange}
            options={toggleOptions}
          />
        )}

        <div
          style={
            isEditMode
              ? undefined
              : { marginTop: 32, paddingTop: 24, borderTop: '1px solid #e9ecef' }
          }
        >
          {values.mode === VIDEO_MODES.EXISTING ? (
            <>
              <Form.GroupHorizontal label={t('selectedItem')}>
                <SelectedItems
                  isLoading={isLoading}
                  items={selectedItems}
                  setItems={(next) => setFieldValue('selectedItems', next)}
                  getItemKey={getVideoKey}
                  isMultiple={false}
                  getItemCardProps={getVideoItemCardProps}
                  onOpenModal={() => setIsModalOpen(true)}
                />
              </Form.GroupHorizontal>
              <SelectItemsModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSelect={(next) => setFieldValue('selectedItems', next)}
                initialSelected={selectedItems}
                items={filteredItems}
                isLoading={isLoading}
                getItemKey={getVideoKey}
                isMultiple={false}
                getItemCardProps={getVideoItemCardProps}
                onSearch={setSearch}
              />
            </>
          ) : (
            <VideoCustomForm />
          )}
        </div>
      </>
    );
  };

  return VideoConfig;
};
